// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import React, { useEffect, useMemo, useState } from "react";
import Header from "../header/HeaderComponent";
import Footer from "../footer/FooterComponent";
import { hiraganas } from "../../consts/hiraganaList";
import { katakana } from "../../consts/katakana";
import { buildRomajiMap, convertRomajiSentence } from "../../utils/romajiConverter";
import { getWordCorrections } from "../../utils/correctionEngine";
import Seo from "../Seo/SeoComponent";
import * as analytics from "../../utils/analytics";
import styles from "./romajiConverterPage.module.scss";

const examplesByMode = {
    hiragana: [
        { label: "konichiwa", romaji: "konichiwa" },
        { label: "ohayo", romaji: "ohayo" },
        { label: "ogenki desuka?", romaji: "ogenki desuka?" }
    ],
    katakana: [
        { label: "gaming", romaji: "geemingu" },
        { label: "gardening", romaji: "gaadeningu" }
    ]
};

function RomajiConverterPage() {
    const [mode, setMode] = useState("hiragana");
    const [input, setInput] = useState("");
    const [copyError, setCopyError] = useState(false);
    const [completedInput, setCompletedInput] = useState("");
    const [dismissedCorrection, setDismissedCorrection] = useState("");

    const romajiMap = useMemo(
        () => buildRomajiMap(mode === "hiragana" ? hiraganas : katakana),
        [mode]
    );

    const outputSegments = useMemo(
        () => convertRomajiSentence(input, romajiMap, mode === "katakana"),
        [input, romajiMap, mode]
    );

    const output = useMemo(
        () => outputSegments.map((segment) => segment.text).join(""),
        [outputSegments]
    );

    const hasUnconverted = outputSegments.some((segment) => !segment.converted);
    useEffect(() => {
        setDismissedCorrection("");
        const timeout = setTimeout(() => {
            setCompletedInput(input);
            if (input.trim()) {
                analytics.track("converter_used", {
                    mode: `romaji_to_${mode}`,
                    inputLength: input.trim().length,
                    found: !hasUnconverted,
                });
            }
        }, 500);
        return () => clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [input]);

    const corrections = useMemo(() => {
        if (mode !== "hiragana" || !completedInput.trim() || dismissedCorrection === completedInput) return [];
        return getWordCorrections(completedInput, romajiMap);
    }, [completedInput, dismissedCorrection, mode, romajiMap]);

    const examples = examplesByMode[mode];

    function handleModeChange(nextMode) {
        setMode(nextMode);
        setCompletedInput("");
        setDismissedCorrection("");
    }

    function handleExampleClick(romaji) {
        setInput(romaji);
    }

    function handleClear() {
        setInput("");
    }

    function handleCopy() {
        if (!output) return;
        navigator.clipboard
            .writeText(output)
            .then(() => setCopyError(false))
            .catch(() => setCopyError(true));
    }

    return (
        <div className={styles.page}>
            <Seo
                title="Romaji to Kana Converter"
                description="Turn romanized Japanese into hiragana or katakana instantly."
                path="/romaji-converter"
            />
            <Header />

            <main className={styles.main}>
                <section className={styles.pageHeader}>
                    <div className={styles.pageHeaderIntro}>
                        <h1 className={styles.pageTitle}>Romaji to Kana</h1>
                        <p className={styles.pageDescription}>
                            Type romaji and see it converted into hiragana or katakana as you go.
                        </p>
                    </div>

                    {mode === "katakana" && (
                        <div className={styles.pageInfo}>
                            Katakana is typically used for loanwords, like gaming or gardening terms.
                        </div>
                    )}
                </section>

                <div className={styles.innerGrid}>
                    <section className={styles.panelCard}>
                        <div className={styles.panelHeader}>
                            <p className={styles.panelTag}>Input</p>
                            <div className={styles.modeTabs} role="tablist">
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={mode === "hiragana"}
                                    className={`${styles.modeTab} ${mode === "hiragana" ? styles.modeTabActive : ""}`}
                                    onClick={() => handleModeChange("hiragana")}
                                >
                                    Hiragana
                                </button>
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={mode === "katakana"}
                                    className={`${styles.modeTab} ${mode === "katakana" ? styles.modeTabActive : ""}`}
                                    onClick={() => handleModeChange("katakana")}
                                >
                                    Katakana
                                </button>
                            </div>
                        </div>

                        <label htmlFor="romaji-converter-input" className={styles.srOnly}>
                            Romaji input
                        </label>
                        <textarea
                            id="romaji-converter-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="e.g. konichiwa, ohayo, ogenki desuka?"
                            className={styles.textarea}
                            rows={5}
                        />

                        <div className={styles.examplesSection}>
                            <div className={styles.examplesHeader}>
                                <span>Examples</span>
                                <span className={styles.examplesNote}>Tap to fill</span>
                            </div>
                            <div className={styles.examplesRow}>
                                {examples.map((example) => (
                                    <button
                                        key={example.label}
                                        type="button"
                                        className={styles.exampleButton}
                                        onClick={() => handleExampleClick(example.romaji)}
                                    >
                                        {example.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className={styles.actionRow}>
                            <button
                                type="button"
                                className={`${styles.actionButton} ${styles.secondaryButton}`}
                                onClick={handleClear}
                                disabled={!input}
                            >
                                Clear
                            </button>
                            <button
                                type="button"
                                className={`${styles.actionButton} ${styles.primaryButton}`}
                                onClick={handleCopy}
                                disabled={!output}
                            >
                                Copy
                            </button>
                        </div>
                    </section>

                    <section className={styles.panelCard}>
                        <div className={styles.resultHeader}>
                            <div>
                                <p className={styles.panelTag}>Result</p>
                                <h3 className={styles.sectionTitle}>
                                    {mode === "hiragana" ? "Hiragana" : "Katakana"} output
                                </h3>
                            </div>
                            {output && (
                                <button
                                    type="button"
                                    className={`${styles.actionButton} ${styles.secondaryButton}`}
                                    onClick={handleCopy}
                                >
                                    Copy
                                </button>
                            )}
                        </div>

                        {output ? (
                            <>
                                <div className={styles.resultBox}>
                                    {outputSegments.map((segment, index) => (
                                        <span
                                            key={index}
                                            className={segment.converted ? undefined : styles.unconverted}
                                        >
                                            {segment.text}
                                        </span>
                                    ))}
                                </div>
                                {hasUnconverted && (
                                    <p className={styles.unconvertedHint}>
                                        Underlined text couldn&apos;t be converted to {mode}.
                                    </p>
                                )}
                                {corrections.map((correction) => (
                                    <div key={correction.correct} className={styles.correctionHint}>
                                        <p>
                                            Did you mean?{" "}
                                            <span className={styles.correctionKana}>{correction.correct}</span>
                                            {" — "}{correction.reading}{" — "}{correction.romaji}{" — "}
                                            {correction.meaning.toLowerCase()}
                                        </p>
                                        <button
                                            type="button"
                                            className={styles.dismissCorrection}
                                            onClick={() => setDismissedCorrection(completedInput)}
                                        >
                                            Dismiss
                                        </button>
                                    </div>
                                ))}
                                {copyError && (
                                    <p className={styles.copyErrorText} role="alert">
                                        Couldn't copy automatically - try selecting the text above and copying it
                                        manually.
                                    </p>
                                )}
                            </>
                        ) : (
                            <div className={styles.emptyState}>
                                <p>Start typing romaji to see the {mode} conversion here.</p>
                            </div>
                        )}
                    </section>
                </div>
            </main>

            <Footer />
        </div>
    );
}

export default RomajiConverterPage;
